/**
 * The menu bar: every command the editor has, where a hand that has used a notation program
 * before will look for it.
 *
 * It renders whatever `menusFor` hands it and knows nothing about scores. One menu is open
 * at a time; moving across the bar while one is open switches to the next, which is what a
 * menu bar does everywhere else. A click anywhere off the bar closes it, and so does Escape.
 */

import { useCallback, useRef, useState } from "react";
import { isRule } from "../lib/score-menu-model";
import type { Menu, MenuAction } from "../lib/score-menu-model";
import { useDismiss } from "../lib/use-dismiss";

export interface ScoreMenuBarProps {
  readonly menus: readonly Menu[];
}

const TITLE =
  "h-9 rounded-[8px] px-3 text-[13px] text-ink-faded hover:bg-leaf hover:text-ink aria-expanded:bg-leaf aria-expanded:text-ink";

function Item({ action, onRun }: { action: MenuAction; onRun(): void }): JSX.Element {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={action.disabled === true}
      onClick={() => {
        onRun();
        action.run();
      }}
      className="flex w-full items-center gap-3 rounded-[6px] px-2.5 py-1.5 text-left text-[13px] text-ink hover:bg-leaf disabled:cursor-default disabled:text-ink-quiet disabled:hover:bg-transparent"
    >
      <span className="w-3 shrink-0 text-center text-[11px] text-ink-quiet">
        {action.checked === true ? "✓" : ""}
      </span>
      <span className="flex-1 truncate">{action.label}</span>
      {action.hint === undefined ? null : (
        <span className="shrink-0 font-mono text-[11px] text-ink-quiet">{action.hint}</span>
      )}
    </button>
  );
}

export default function ScoreMenuBar({ menus }: ScoreMenuBarProps): JSX.Element {
  const [open, setOpen] = useState<string | null>(null);
  const bar = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setOpen(null), []);
  useDismiss(bar, open !== null, close);

  return (
    <div
      ref={bar}
      role="menubar"
      className="relative z-20 flex items-center gap-0.5 rounded-xl border border-rule bg-white px-1.5 py-1"
    >
      {menus.map((menu) => (
        <div key={menu.title} className="relative">
          <button
            type="button"
            role="menuitem"
            aria-expanded={open === menu.title}
            aria-haspopup="menu"
            className={TITLE}
            onClick={() => setOpen((current) => (current === menu.title ? null : menu.title))}
            onMouseEnter={() => setOpen((current) => (current === null ? null : menu.title))}
          >
            {menu.title}
          </button>

          {open !== menu.title ? null : (
            <div
              role="menu"
              aria-label={menu.title}
              className="absolute left-0 top-full z-30 mt-1 max-h-[min(70vh,520px)] min-w-[248px] overflow-y-auto rounded-[10px] border border-rule bg-white p-1 shadow-[0_12px_32px_rgba(0,0,0,0.12)]"
            >
              {menu.items.map((entry, index) =>
                isRule(entry) ? (
                  <div key={`rule-${index}`} className="my-1 border-t border-rule" />
                ) : (
                  <Item key={entry.label} action={entry} onRun={() => setOpen(null)} />
                ),
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
